import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { LanguageMode } from '../types';

export const JSON_VALIDATION_DEBOUNCE_MS = 300;

export const useJsonValidation = (
  language: LanguageMode,
  disableJsonValidation: boolean,
  onChange?: (value: string, error: string | null | undefined) => void,
  onError?: (error: string | null) => void
) => {
  const [error, setError] = useState<string | null>(null);

  const debouncedValidation = useMemo(
    () =>
      debounce((newValue: string) => {
        if (language === 'json' && !disableJsonValidation) {
          try {
            JSON.parse(newValue);
            setError(null);
            onError?.(null);
            onChange?.(newValue, null);
          } catch (e) {
            const errorMessage =
              e instanceof Error ? e.message : 'Invalid JSON';
            setError(errorMessage);
            onError?.(errorMessage);
            onChange?.(newValue, errorMessage);
          }
        } else {
          setError(null);
          onError?.(null);
          onChange?.(newValue, null);
        }
      }, JSON_VALIDATION_DEBOUNCE_MS),
    [onChange, onError, disableJsonValidation, language]
  );

  const handleChange = useCallback(
    (newValue: string) => {
      onChange?.(newValue, undefined);
      debouncedValidation(newValue);
    },
    [debouncedValidation, onChange]
  );

  const cancelPendingValidation = useCallback(() => {
    debouncedValidation.cancel();
  }, [debouncedValidation]);

  const clearError = useCallback(() => {
    setError(null);
    onError?.(null);
  }, [onError]);

  useEffect(
    () => () => {
      debouncedValidation.cancel();
    },
    [debouncedValidation]
  );

  return { error, handleChange, cancelPendingValidation, clearError };
};
